import React, { useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { FormProvider, useForm } from 'react-hook-form';
import { merge } from 'lodash';

import { AdvancedSettingsSubForm } from './AdvancedSettingsSubForm';
import { BasicSettingsSubForm } from './BasicSettingsSubForm';
import { DEFAULT_ADVANCED_CONFIG } from './defaultValues';
import type { TVideoCheckFormSubmitValues, TVideoCheckParameters, TVideoCheckFormDefaultValues } from '../types';

export type TVideoCheckFormOnSubmit = (values: TVideoCheckFormSubmitValues) => void;
export interface VideoCheckFormProps {
  onSubmit: TVideoCheckFormOnSubmit;
  onCancel: VoidFunction;
}

const defaultValues = {
  fileList: undefined,
  liveness: false,
  deepfake: false,
  face: false,
  body: false,
  parameters: DEFAULT_ADVANCED_CONFIG,
};

function VideoChecksForm({ onCancel, onSubmit, ...formProps }: VideoCheckFormProps): React.JSX.Element {
  const initialValues = merge({}, defaultValues, formProps);
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);
  const [advancedSnapshot, setAdvancedSnapshot] = useState<TVideoCheckParameters>(DEFAULT_ADVANCED_CONFIG);

  const form = useForm<TVideoCheckFormDefaultValues>({
    defaultValues: initialValues,
    shouldUnregister: false,
  });

  const handleOpenAdvanced = () => {
    const currentParams = cloneDeep(form.getValues('parameters'));
    setAdvancedSnapshot(currentParams);
    setAdvancedOpen(true);
  };

  const handleAdvancedCancel = () => {
    const [fileList, liveness, deepfake, face, body] = form.getValues([
      'fileList',
      'liveness',
      'deepfake',
      'face',
      'body',
    ]);

    form.reset({
      fileList,
      liveness,
      deepfake,
      face,
      body,
      parameters: advancedSnapshot,
    });

    setAdvancedOpen(false);
  };

  const handleAdvancedSave = async () => {
    const isValid = await form.trigger(['parameters']);
    if (isValid) setAdvancedOpen(false);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {!advancedOpen && (
          <BasicSettingsSubForm
            onCancel={onCancel}
            onOpenSettings={handleOpenAdvanced}
          />
        )}
        {!!advancedOpen && (
          <AdvancedSettingsSubForm
            onCancel={handleAdvancedCancel}
            onSave={handleAdvancedSave}
          />
        )}
      </form>
    </FormProvider>
  );
}

export { VideoChecksForm };
